/**
 * CDP SDK News Plugin
 * Article and content tracking for news sites and blogs
 */

import type { CDPCoreAPI, CDPPlugin, SiteProfile } from '../../core/types';
import type {
  NewsPluginConfig,
  ArticleMetadata,
  ReadingState,
} from './types';
import {
  DEFAULT_NEWS_CONFIG,
  DEFAULT_SHARE_SELECTORS,
} from './types';
import { isArticlePage, extractArticleMetadata, getPageType } from './detector';
import { createReadingTracker, ReadingTrackerInstance } from './reading';
import {
  NEWS_EVENTS,
  buildArticleViewedProperties,
  buildArticleEngagedProperties,
  buildArticleReadProperties,
  buildArticleReadingSessionProperties,
  buildArticleScrollMilestoneProperties,
  buildArticleSharedProperties,
  buildCommentPostedProperties,
  buildArticleSavedProperties,
  buildSearchPerformedProperties,
  detectShareDestination,
} from './events';
import { addListener, $$ } from '../base';

// =============================================================================
// PLUGIN FACTORY
// =============================================================================

/**
 * Create News Plugin
 */
export function createNewsPlugin(config: NewsPluginConfig = {}): CDPPlugin {
  const pluginConfig: Required<NewsPluginConfig> = {
    ...DEFAULT_NEWS_CONFIG,
    ...config,
    selectors: { ...DEFAULT_NEWS_CONFIG.selectors, ...config.selectors },
    engageThreshold: { ...DEFAULT_NEWS_CONFIG.engageThreshold, ...config.engageThreshold },
    readThreshold: { ...DEFAULT_NEWS_CONFIG.readThreshold, ...config.readThreshold },
  };

  let core: CDPCoreAPI | null = null;
  let readingTracker: ReadingTrackerInstance | null = null;
  let currentArticle: ArticleMetadata | null = null;
  let cleanupFns: (() => void)[] = [];
  // Set synchronously on each (soft-)navigation so the core `page` event can be
  // suppressed on article pages before it is sent (see the beforeSend in install).
  let currentPageIsArticle = false;

  /**
   * Merge the relevant remote Site Profile fields into the plugin config.
   * Called synchronously on each navigation (and again once the cold-start
   * profile lands), so selectors / taxonomy / language / event toggles all
   * come from the centrally-managed profile.
   */
  function applyProfile(profile: SiteProfile): void {
    if (profile.selectors) {
      pluginConfig.selectors = { ...pluginConfig.selectors, ...profile.selectors };
    }
    if (profile.categoryMap) {
      pluginConfig.categoryMap = { ...pluginConfig.categoryMap, ...profile.categoryMap };
    }
    if (profile.urlTaxonomy) pluginConfig.urlTaxonomy = profile.urlTaxonomy;
    if (profile.language) pluginConfig.language = profile.language;
    const e = profile.events;
    if (e) {
      if (e.scrollMilestones !== undefined) pluginConfig.emitScrollMilestones = e.scrollMilestones;
      if (e.suppressPageOnArticle !== undefined) pluginConfig.suppressPageOnArticle = e.suppressPageOnArticle;
      if (e.trackEngaged !== undefined) pluginConfig.trackEngaged = e.trackEngaged;
      if (e.trackRead !== undefined) pluginConfig.trackRead = e.trackRead;
      if (e.trackShares !== undefined) pluginConfig.trackShares = e.trackShares;
      // M32 / §S4 and §S6 — both remote so a site can be switched without an
      // SDK release, and switched BACK just as fast if something reads them.
      if (e.collectTags !== undefined) pluginConfig.collectTags = e.collectTags;
      if (e.leanProperties !== undefined) pluginConfig.leanProperties = e.leanProperties;
    }
  }

  /**
   * Detect the page synchronously (so suppression is decided before the core
   * page event fires), then track the article. On a cold start — the remote
   * profile hasn't loaded yet — defer the actual tracking until it lands so the
   * site's taxonomy/selectors are applied to the very first article view.
   */
  function detectAndTrack(): void {
    if (!core) return;

    // Apply whatever profile we have now, then detect (default selectors detect
    // an article via og:type / itemtype even before site selectors are merged).
    applyProfile(core.getSiteProfile());
    currentPageIsArticle = getPageType(pluginConfig.selectors) === 'article';
    if (!currentPageIsArticle) {
      if (pluginConfig.debugArticles) {
        core.log('News Plugin: not an article page');
      }
      return;
    }

    const profile = core.getSiteProfile();
    const remoteOn = core.getConfig()?.remoteConfig !== false;
    const cold = remoteOn && profile.version == null;

    if (cold) {
      // Wait for the (timeout-bounded) profile fetch so category/selectors are
      // correct on the first ever visit; fail-open if it never arrives.
      void core.whenConfigReady().then((p) => {
        applyProfile(p);
        trackArticleView();
      });
    } else {
      trackArticleView();
    }
  }

  /**
   * Track article view and setup reading tracking
   */
  function trackArticleView(): void {
    if (!core) return;

    const pageType = getPageType(pluginConfig.selectors);

    if (pageType !== 'article') {
      if (pluginConfig.debugArticles) {
        core.log('News Plugin: Not an article page, page type:', pageType);
      }
      return;
    }

    // Extract article metadata
    currentArticle = extractArticleMetadata(pluginConfig);

    if (!currentArticle) {
      if (pluginConfig.debugArticles) {
        core.log('News Plugin: Could not extract article metadata');
      }
      return;
    }

    if (pluginConfig.debugArticles) {
      core.log('News Plugin: Article detected:', currentArticle);
    }

    // Track Article Viewed event
    core.track(
      NEWS_EVENTS.ARTICLE_VIEWED,
      buildArticleViewedProperties(currentArticle, pluginConfig) as unknown as Record<string, unknown>
    );

    // Setup reading tracker
    setupReadingTracker(currentArticle);

    // Setup share tracking
    if (pluginConfig.trackShares) {
      setupShareTracking(currentArticle);
    }
  }

  /**
   * Setup reading tracker for an article
   */
  function setupReadingTracker(article: ArticleMetadata): void {
    if (!core) return;

    // Clean up existing tracker
    if (readingTracker) {
      readingTracker.destroy();
      readingTracker = null;
    }

    readingTracker = createReadingTracker(
      article,
      {
        onEngage: (state: ReadingState) => {
          if (!core || !currentArticle) return;
          if (!pluginConfig.trackEngaged) return;

          const trigger = readingTracker?.getEngageTrigger() || 'time';
          core.track(
            NEWS_EVENTS.ARTICLE_ENGAGED,
            buildArticleEngagedProperties(currentArticle, pluginConfig, state, trigger) as unknown as Record<string, unknown>
          );
        },

        onRead: (state: ReadingState) => {
          if (!core || !currentArticle || !readingTracker) return;
          if (!pluginConfig.trackRead) return;

          const engageTrigger = readingTracker.getEngageTrigger() || 'time';
          const readTrigger = readingTracker.getReadTrigger() || 'time';

          core.track(
            NEWS_EVENTS.ARTICLE_READ,
            buildArticleReadProperties(
              currentArticle,
              pluginConfig,
              state,
              engageTrigger,
              readTrigger
            ) as unknown as Record<string, unknown>
          );
        },

        onMilestone: (milestone: number, state: ReadingState) => {
          if (!core || !currentArticle) return;
          // Lean mode: milestones are folded into the Article Reading Session
          // event (with timing); skip the separate per-milestone event. The
          // reach + timing are still recorded on the reading state.
          if (!pluginConfig.emitScrollMilestones) return;

          const timeToMilestone = Math.round(state.totalTimeMs / 1000);

          core.track(
            NEWS_EVENTS.ARTICLE_SCROLL_MILESTONE,
            buildArticleScrollMilestoneProperties(
              currentArticle,
              pluginConfig,
              milestone,
              timeToMilestone
            ) as unknown as Record<string, unknown>
          );
        },

        onSessionEnd: (state: ReadingState) => {
          if (!core || !currentArticle || !readingTracker) return;

          const visibleTime = readingTracker.getVisibleTimeSeconds();

          // Use sendBeacon for reliable delivery on page unload
          const properties = buildArticleReadingSessionProperties(
            currentArticle,
            pluginConfig,
            state,
            visibleTime
          );

          // Create event payload manually for beacon
          core.track(NEWS_EVENTS.ARTICLE_READING_SESSION, properties as unknown as Record<string, unknown>);
        },
      },
      {
        engageThreshold: pluginConfig.engageThreshold,
        readThreshold: pluginConfig.readThreshold,
        milestones: pluginConfig.scrollMilestones,
        selectors: pluginConfig.selectors,
      }
    );
  }

  /**
   * Setup share button tracking
   */
  function setupShareTracking(article: ArticleMetadata): void {
    const selectors = pluginConfig.shareSelectors || DEFAULT_SHARE_SELECTORS;
    const combinedSelector = selectors.join(', ');

    const shareButtons = $$(combinedSelector);

    for (const button of shareButtons) {
      const cleanup = addListener(button as HTMLElement, 'click', (event) => {
        if (!core) return;

        const destination = detectShareDestination(
          event.currentTarget as Element,
          (event.currentTarget as HTMLAnchorElement)?.href
        );

        core.track(
          NEWS_EVENTS.ARTICLE_SHARED,
          buildArticleSharedProperties(article, pluginConfig, 'button', destination) as unknown as Record<string, unknown>
        );
      });

      cleanupFns.push(cleanup);
    }
  }

  /**
   * Cleanup current article tracking
   */
  function cleanupArticle(): void {
    if (readingTracker) {
      readingTracker.destroy();
      readingTracker = null;
    }

    for (const cleanup of cleanupFns) {
      cleanup();
    }
    cleanupFns = [];

    currentArticle = null;
    currentPageIsArticle = false;
  }

  /**
   * Track a comment being posted on the current article
   */
  function trackComment(commentId?: string, parentCommentId?: string, commentText?: string): void {
    if (!core || !currentArticle) return;

    core.track(
      NEWS_EVENTS.COMMENT_POSTED,
      buildCommentPostedProperties(
        currentArticle,
        pluginConfig,
        commentId,
        parentCommentId,
        commentText?.length
      ) as unknown as Record<string, unknown>
    );
  }

  /**
   * Track an article being saved/bookmarked
   */
  function trackSave(saveLocation?: string): void {
    if (!core || !currentArticle) return;

    core.track(
      NEWS_EVENTS.ARTICLE_SAVED,
      buildArticleSavedProperties(
        currentArticle,
        pluginConfig,
        saveLocation
      ) as unknown as Record<string, unknown>
    );
  }

  /**
   * Track a search being performed
   */
  function trackSearch(query: string, resultsCount?: number, searchType?: string, filters?: Record<string, string>): void {
    if (!core) return;

    core.track(
      NEWS_EVENTS.SEARCH_PERFORMED,
      buildSearchPerformedProperties(
        query,
        pluginConfig,
        resultsCount,
        searchType,
        filters
      ) as unknown as Record<string, unknown>
    );
  }

  /**
   * Track an article share manually
   */
  function trackShare(shareMethod?: string, shareDestination?: string): void {
    if (!core || !currentArticle) return;

    core.track(
      NEWS_EVENTS.ARTICLE_SHARED,
      buildArticleSharedProperties(currentArticle, pluginConfig, shareMethod, shareDestination) as unknown as Record<string, unknown>
    );
  }

  return {
    name: 'news',
    version: '1.0.0',

    // Manual tracking methods (exposed for site integration)
    trackComment,
    trackSave,
    trackSearch,
    trackShare,

    install(cdp: CDPCoreAPI): void {
      core = cdp;
      // Suppress the generic core `page` event on article pages — "Article
      // Viewed" already carries the same context. currentPageIsArticle is set
      // synchronously (in detectAndTrack) before the core page event fires.
      cdp.onBeforeSend((payload) => {
        if (
          payload.type === 'page' &&
          currentPageIsArticle &&
          pluginConfig.suppressPageOnArticle
        ) {
          return null;
        }
        return payload;
      });
    },

    onInit(): void {
      detectAndTrack();
    },

    onPageChange(url: string): void {
      // Same article under a new URL (live-blog pushState, gallery slide
      // hash/query, share-param rewrites) is NOT a new read: keep the tracker
      // and the session. Measured 2026-08-20 on dir.bg: 1,850 visitor×article
      // pairs/hour with 2+ sessions, median 9 s apart, 61% on the identical
      // path — a quarter of all its reading sessions were these splits.
      if (currentArticle && currentPageIsArticle && isArticlePage(pluginConfig.selectors)) {
        const next = extractArticleMetadata(pluginConfig);
        if (next && next.articleId && next.articleId === currentArticle.articleId) {
          if (pluginConfig.debugArticles) core?.log('News Plugin: same article, URL changed — session continues', url);
          return;
        }
      }
      // Clean up the previous article, then detect + track the new page.
      cleanupArticle();
      detectAndTrack();
    },

    onHidden(): void {
      // Tab hidden, page still alive: emit a cumulative checkpoint and keep
      // the tracker + share/comment listeners running for when it returns.
      // Destroying here (the pre-2026-08-20 behaviour) truncated the session
      // at the first tab-hide and started a NEW one on return.
      if (!core || !currentArticle || !readingTracker) return;
      const snap = readingTracker.checkpoint();
      if (!snap) return;
      core.track(
        NEWS_EVENTS.ARTICLE_READING_CHECKPOINT,
        buildArticleReadingSessionProperties(
          currentArticle,
          pluginConfig,
          snap,
          readingTracker.getVisibleTimeSeconds(),
          true
        ) as unknown as Record<string, unknown>
      );
    },

    onUnload(): void {
      // Real page exit: the tracker's destroy callback emits the final
      // Article Reading Session.
      cleanupArticle();
    },

    uninstall(): void {
      cleanupArticle();
      core = null;
    },
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

export { NEWS_EVENTS } from './events';
export type {
  NewsPluginConfig,
  ArticleMetadata,
  ArticleSelectors,
  ReadingThreshold,
  ReadingState,
  ArticleViewedProperties,
  ArticleEngagedProperties,
  ArticleReadProperties,
  ArticleReadingSessionProperties,
  ArticleScrollMilestoneProperties,
  ArticleSharedProperties,
  CommentPostedProperties,
  ArticleSavedProperties,
  SearchPerformedProperties,
} from './types';
export {
  DEFAULT_NEWS_CONFIG,
  DEFAULT_ARTICLE_SELECTORS,
  DEFAULT_ENGAGE_THRESHOLD,
  DEFAULT_READ_THRESHOLD,
  DEFAULT_SCROLL_MILESTONES,
} from './types';
export { isArticlePage, extractArticleMetadata, getPageType } from './detector';

// Default export
export default createNewsPlugin;
